import type { Directive } from "vue";

const suppressNextClick = () => {
	const stop = (event: MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
	};
	window.addEventListener("click", stop, { capture: true, once: true });
	window.setTimeout(() => window.removeEventListener("click", stop, { capture: true }), 0);
};

export const vDragScroll: Directive<HTMLElement & { _dragScrollCleanup: () => void }> = {
	mounted(el) {
		let isDown = false;
		let startX: number;
		let startY: number;
		let scrollLeft: number;
		let scrollTop: number;
		let rafId: number;
		let velocityX = 0;
		let velocityY = 0;
		let lastX = 0;
		let lastY = 0;
		let lastTime = 0;
		let hasMoved = false;
		let savedSnapType: string | null = null;
		const MOVE_THRESHOLD = 5;

		const pauseSnap = () => {
			if (savedSnapType !== null) return;
			savedSnapType = el.style.scrollSnapType;
			el.style.scrollSnapType = "none";
		};

		const restoreSnap = () => {
			if (savedSnapType === null) return;
			el.style.scrollSnapType = savedSnapType;
			savedSnapType = null;
		};

		const stopDrag = () => {
			el.style.pointerEvents = "";
			isDown = false;
			hasMoved = false;
			restoreSnap();
		};

		el.style.cursor = "grab";

		const onMouseDown = (e: MouseEvent) => {
			if (e.button !== 0) return;

			if (rafId) {
				cancelAnimationFrame(rafId);
				rafId = 0;
			}

			isDown = true;
			hasMoved = false;

			startX = e.pageX - el.offsetLeft;
			startY = e.pageY - el.offsetTop;
			scrollLeft = el.scrollLeft;
			scrollTop = el.scrollTop;

			lastX = e.pageX;
			lastY = e.pageY;
			lastTime = Date.now();

			velocityX = 0;
			velocityY = 0;
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!isDown) return;
			if (e.buttons === 0) {
				stopDrag();
				return;
			}

			const currentX = e.pageX;
			const currentY = e.pageY;

			const deltaMoveX = Math.abs(currentX - startX);
			const deltaMoveY = Math.abs(currentY - startY);

			if (!hasMoved && (deltaMoveX > MOVE_THRESHOLD || deltaMoveY > MOVE_THRESHOLD)) {
				hasMoved = true;
				el.style.pointerEvents = "none";
				pauseSnap();
			}

			if (hasMoved) {
				e.stopPropagation();
				e.preventDefault();

				const now = Date.now();
				const deltaTime = now - lastTime;

				if (deltaTime > 0) {
					velocityX = ((currentX - lastX) / deltaTime) * 16;
					velocityY = ((currentY - lastY) / deltaTime) * 16;
				}

				lastX = currentX;
				lastY = currentY;
				lastTime = now;

				const x = currentX - el.offsetLeft;
				const y = currentY - el.offsetTop;

				const deltaX = x - startX;
				const deltaY = y - startY;

				el.scrollLeft = scrollLeft - deltaX;
				el.scrollTop = scrollTop - deltaY;
			} else {
				lastX = currentX;
				lastY = currentY;
				lastTime = Date.now();
			}
		};

		const onMouseUp = (e: MouseEvent) => {
			if (!isDown) return;

			if (hasMoved) {
				e.stopPropagation();
				e.preventDefault();
				suppressNextClick();

				el.style.pointerEvents = "";

				if (Math.abs(velocityX) > 0.1 || Math.abs(velocityY) > 0.1) {
					const inertia = () => {
						el.scrollLeft -= velocityX;
						el.scrollTop -= velocityY;
						velocityX *= 0.95;
						velocityY *= 0.95;

						if (Math.abs(velocityX) > 0.1 || Math.abs(velocityY) > 0.1) {
							rafId = requestAnimationFrame(inertia);
						} else {
							rafId = 0;
							restoreSnap();
						}
					};

					rafId = requestAnimationFrame(inertia);
				} else {
					restoreSnap();
				}
			} else {
				el.style.pointerEvents = "";
			}

			isDown = false;
			hasMoved = false;
		};

		el.addEventListener("mousedown", onMouseDown);
		window.addEventListener("mousemove", onMouseMove);
		window.addEventListener("mouseup", onMouseUp);

		el._dragScrollCleanup = () => {
			if (rafId) cancelAnimationFrame(rafId);
			el.removeEventListener("mousedown", onMouseDown);
			window.removeEventListener("mousemove", onMouseMove);
			window.removeEventListener("mouseup", onMouseUp);
			el.style.pointerEvents = "";
			restoreSnap();
		};
	},

	unmounted(el) {
		if (el._dragScrollCleanup) {
			el._dragScrollCleanup();
		}
	},
};
